import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiTrash2, FiRefreshCw, FiEye, FiEyeOff, FiUser, FiUsers } from 'react-icons/fi';
import api from '../../services/api';
import { toast } from 'react-toastify';

const NotificationsList = () => {
  const [filter, setFilter] = useState('all'); // 'all', 'personal', 'global'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const queryClient = useQueryClient();

  // Récupérer les notifications de l'utilisateur (personnelles)
  const { data: personalNotifications, isLoading: personalLoading, refetch: refetchPersonal } = useQuery(
    'personal-notifications',
    () => api.get('/notifications').then(res => res.data.data || []),
    {
      enabled: filter === 'all' || filter === 'personal'
    }
  );

  // Récupérer les notifications globales
  const { data: globalNotifications, isLoading: globalLoading, refetch: refetchGlobal } = useQuery(
    'global-notifications',
    () => api.get('/notifications/global').then(res => res.data.data || []),
    {
      enabled: filter === 'all' || filter === 'global'
    }
  );

  // Mutations pour les actions
  const markAsReadMutation = useMutation(
    (id) => api.put(`/notifications/${id}/mark-read`),
    {
      onSuccess: () => {
        refetchPersonal();
        toast.success('Notification marquée comme lue');
      },
      onError: () => {
        toast.error('Erreur lors de la mise à jour');
      }
    }
  );

  const deletePersonalMutation = useMutation(
    (id) => api.delete(`/notifications/${id}`),
    {
      onSuccess: () => {
        refetchPersonal();
        toast.success('Notification supprimée');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      }
    }
  );

  const deleteGlobalMutation = useMutation(
    (id) => api.delete(`/notifications/global/${id}`),
    {
      onSuccess: () => {
        refetchGlobal();
        toast.success('Notification globale supprimée');
      },
      onError: () => {
        toast.error('Erreur lors de la suppression');
      }
    }
  );

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour obtenir la couleur selon le type
  const getTypeColor = (type) => {
    const colors = {
      info: 'bg-blue-100 text-blue-800',
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Combiner et filtrer les notifications
  const getFilteredNotifications = () => {
    let notifications = [];

    if (filter === 'all' || filter === 'personal') {
      const personal = (personalNotifications || []).map(notification => ({
        ...notification,
        notificationType: 'Personnelle',
        icon: FiUser
      }));
      notifications = [...notifications, ...personal];
    }

    if (filter === 'all' || filter === 'global') {
      const global = (globalNotifications || []).map(notification => ({
        ...notification,
        notificationType: 'Globale',
        icon: FiUsers
      }));
      notifications = [...notifications, ...global];
    }

    // Trier par date de création (plus récent en premier)
    return notifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  };

  const filteredNotifications = getFilteredNotifications();

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(startIndex, startIndex + itemsPerPage);

  const handleMarkAsRead = (notification) => {
    if (notification.notificationType === 'Personnelle') {
      markAsReadMutation.mutate(notification.id);
    }
  };

  const handleDelete = (notification) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette notification ?')) {
      if (notification.notificationType === 'Personnelle') {
        deletePersonalMutation.mutate(notification.id);
      } else {
        deleteGlobalMutation.mutate(notification.id);
      }
    }
  };

  const handleRefresh = () => {
    refetchPersonal();
    refetchGlobal();
    toast.info('Données actualisées');
  };

  const isLoading = personalLoading || globalLoading;

  return (
    <div className="space-y-6">
      {/* Filtres et actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Liste des notifications
          </h3>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FiRefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
        </div>

        {/* Filtres */}
        <div className="flex items-center space-x-4 mb-4">
          <span className="text-sm font-medium text-gray-700">Filtrer par :</span>
          <div className="flex space-x-2">
            {[
              { value: 'all', label: 'Toutes' },
              { value: 'personal', label: 'Personnelles' },
              { value: 'global', label: 'Globales' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-3 py-1 text-sm rounded-full ${
                  filter === option.value
                    ? 'bg-blue-100 text-blue-800 border border-blue-300'
                    : 'bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-lg font-semibold text-blue-600">
              {filteredNotifications.filter(n => n.notificationType === 'Personnelle').length}
            </div>
            <div className="text-sm text-blue-800">Notifications personnelles</div>
          </div>
          <div className="bg-green-50 p-3 rounded-lg">
            <div className="text-lg font-semibold text-green-600">
              {filteredNotifications.filter(n => n.notificationType === 'Globale').length}
            </div>
            <div className="text-sm text-green-800">Notifications globales</div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-lg">
            <div className="text-lg font-semibold text-yellow-600">
              {filteredNotifications.filter(n => !n.is_read && n.notificationType === 'Personnelle').length}
            </div>
            <div className="text-sm text-yellow-800">Non lues</div>
          </div>
        </div>
      </div>

      {/* Tableau des notifications */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Titre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Créée le
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedNotifications.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        <FiRefreshCw className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        Aucune notification trouvée
                      </td>
                    </tr>
                  ) : (
                    paginatedNotifications.map((notification) => {
                      const IconComponent = notification.icon;
                      return (
                        <tr key={`${notification.notificationType}-${notification.id}`} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <IconComponent className="h-5 w-5 text-gray-400 mr-2" />
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                notification.notificationType === 'Personnelle'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                              }`}>
                                {notification.notificationType}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {notification.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeColor(notification.type)}`}>
                                {notification.type}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {notification.message}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {notification.notificationType === 'Personnelle' && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                notification.is_read
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {notification.is_read ? (
                                  <>
                                    <FiEye className="h-3 w-3 mr-1" />
                                    Lue
                                  </>
                                ) : (
                                  <>
                                    <FiEyeOff className="h-3 w-3 mr-1" />
                                    Non lue
                                  </>
                                )}
                              </span>
                            )}
                            {notification.notificationType === 'Globale' && (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                notification.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {notification.is_active ? 'Active' : 'Inactive'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(notification.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {notification.notificationType === 'Personnelle' && !notification.is_read && (
                                <button
                                  onClick={() => handleMarkAsRead(notification)}
                                  disabled={markAsReadMutation.isLoading}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Marquer comme lue"
                                >
                                  <FiEye className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(notification)}
                                disabled={
                                  (notification.notificationType === 'Personnelle' && deletePersonalMutation.isLoading) ||
                                  (notification.notificationType === 'Globale' && deleteGlobalMutation.isLoading)
                                }
                                className="text-red-600 hover:text-red-900 p-1"
                                title="Supprimer"
                              >
                                <FiTrash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Précédent
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Suivant
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Affichage de{' '}
                      <span className="font-medium">{startIndex + 1}</span>
                      {' '}à{' '}
                      <span className="font-medium">
                        {Math.min(startIndex + itemsPerPage, filteredNotifications.length)}
                      </span>
                      {' '}sur{' '}
                      <span className="font-medium">{filteredNotifications.length}</span>
                      {' '}résultats
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Précédent</span>
                        ‹
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          const distance = Math.abs(page - currentPage);
                          return distance === 0 || distance === 1 || page === 1 || page === totalPages;
                        })
                        .map((page, index, array) => (
                          <React.Fragment key={page}>
                            {index > 0 && array[index - 1] !== page - 1 && (
                              <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                              </span>
                            )}
                            <button
                              onClick={() => setCurrentPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          </React.Fragment>
                        ))}
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Suivant</span>
                        ›
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsList;
